import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { storage } from '../firebase/config';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

// Storage path for logo
const LOGO_STORAGE_PATH = 'branding/logo';
const BRANDING_DOC_ID = 'branding';

/**
 * Logo Storage Service
 */
export const logoStorageService = {
  /**
   * Upload logo to Firebase Storage
   * @param {File} file - The logo file to upload
   * @returns {Promise<string>} - The download URL of the uploaded logo
   */
  async uploadLogo(file) {
    // ALWAYS try Firebase Storage first - storage rules now allow unauthenticated writes to branding/
    // This ensures the logo is stored in Firebase and accessible from any browser/device
    try {
      // Get file extension
      const fileExt = file.name.split('.').pop().toLowerCase();
      const storageRef = ref(storage, `${LOGO_STORAGE_PATH}.${fileExt}`);
      
      // Upload file to Firebase Storage
      await uploadBytes(storageRef, file);
      
      // Get download URL from Firebase Storage
      const downloadURL = await getDownloadURL(storageRef);
      
      console.log('Logo uploaded to Firebase Storage successfully:', downloadURL);
      
      // Save URL to Firestore for persistence across devices and sessions
      await this.saveLogoURL(downloadURL);
      
      return downloadURL;
    } catch (error) {
      // If Firebase Storage upload fails, throw error - we don't want base64 fallback
      // Base64 is too large and doesn't work across browsers/devices
      console.error('Firebase Storage upload failed:', error);
      throw new Error(`Failed to upload logo to Firebase Storage: ${error.message}. Please ensure you have internet connectivity and Firebase Storage is properly configured.`);
    }
  },

  /**
   * Get logo URL from Firestore (primary source) or localStorage (cache)
   * @returns {Promise<string|null>} - The logo URL or null if not found
   */
  async getLogoURL() {
    // ALWAYS check Firestore first - this is the source of truth
    // Firestore rules allow public read access for the logo
    // This ensures persistence across devices, browsers, and sessions
    try {
      const brandingRef = doc(db, 'settings', BRANDING_DOC_ID);
      const snapshot = await getDoc(brandingRef);
      
      if (snapshot.exists()) {
        const data = snapshot.data();
        
        if (data.logoUrl) {
          const logoUrl = data.logoUrl;
          
          if (logoUrl.startsWith('data:')) {
            console.warn('Found base64 logo in Firestore - consider re-uploading via Settings for better performance');
          }
          
          console.log('Logo URL retrieved from Firestore:', logoUrl);
          return logoUrl;
        } else {
          console.log('Firestore document exists but no logoUrl field found');
        }
      } else {
        console.log('Firestore branding document does not exist yet');
      }
    } catch (error) {
      console.error('Error getting logo URL from Firestore:', error.code, error.message);
      if (error.code === 'permission-denied') {
        console.error('Permission denied accessing Firestore. Please ensure Firebase rules are deployed.');
      }
      return null;
    }
    
    console.log('No logo found in Firestore');
    return null;
  },

  /**
   * Delete logo from Firebase Storage
   * @returns {Promise<void>}
   */
  async deleteLogo() {
    try {
      // Try to get the current logo URL to determine the file extension
      const currentUrl = await this.getLogoURL();
      
      // Only try to delete from Storage if it's a Storage URL (not base64)
      if (currentUrl && !currentUrl.startsWith('data:')) {
        // Extract file extension from URL or try common extensions
        const urlParts = currentUrl.split('.');
        const possibleExt = urlParts[urlParts.length - 1].split('?')[0].toLowerCase(); // Remove query params
        
        // Try to delete with the extension we found
        if (possibleExt && possibleExt.length <= 5) {
          try {
            const storageRef = ref(storage, `${LOGO_STORAGE_PATH}.${possibleExt}`);
            await deleteObject(storageRef);
            console.log('Logo deleted from Firebase Storage');
          } catch (deleteError) {
            // If deletion fails, try common extensions
            console.warn('Failed to delete with detected extension, trying common extensions:', deleteError);
            const commonExts = ['png', 'jpg', 'jpeg', 'svg', 'webp'];
            for (const ext of commonExts) {
              try {
                const storageRef = ref(storage, `${LOGO_STORAGE_PATH}.${ext}`);
                await deleteObject(storageRef);
                console.log(`Logo deleted from Firebase Storage with extension: ${ext}`);
                break;
              } catch (e) {
                // Continue to next extension
              }
            }
          }
        }
      }
      
      // Remove from Firestore
      await this.removeLogoURL();
      
      console.log('Logo deleted successfully');
    } catch (error) {
      console.warn('Error deleting logo from Storage:', error);
      // Still remove from Firestore even if storage deletion fails
      await this.removeLogoURL();
      // Don't throw - cleanup is sufficient
    }
  },

  /**
   * Save logo URL to Firestore
   * @param {string} logoUrl - The logo download URL
   * @private
   */
  async saveLogoURL(logoUrl) {
    try {
      const brandingRef = doc(db, 'settings', BRANDING_DOC_ID);
      await setDoc(brandingRef, {
        logoUrl,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      console.log('Logo URL saved to Firestore successfully');
    } catch (error) {
      console.error('Error saving logo URL to Firestore:', error.message);
      throw error; // Throw error - no localStorage fallback
    }
  },

  /**
   * Remove logo URL from Firestore
   * @private
   */
  async removeLogoURL() {
    try {
      const brandingRef = doc(db, 'settings', BRANDING_DOC_ID);
      await updateDoc(brandingRef, {
        logoUrl: null,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error removing logo URL from Firestore:', error.message);
      throw error; // Throw error - no localStorage fallback
    }
  }
};

/**
 * Upload inspection / completion videos for a vehicle job (Firebase Storage: vehicles/{reg}/...)
 * @param {File} file
 * @param {string} vehicleReg - display registration (will be sanitized for path)
 * @param {'inspection'|'final'} kind
 * @returns {Promise<string>} download URL
 */
export const vehicleMediaService = {
  async uploadVideo(file, vehicleReg, kind) {
    if (!file || !file.size) {
      throw new Error('No file selected');
    }
    const safeReg = String(vehicleReg || 'unknown')
      .replace(/[^a-zA-Z0-9_-]/g, '_')
      .slice(0, 48);
    const rawExt = (file.name && file.name.includes('.'))
      ? file.name.split('.').pop().toLowerCase()
      : 'webm';
    const ext = ['webm', 'mp4', 'mov', 'm4v', 'mkv'].includes(rawExt) ? rawExt : 'mp4';
    const name = `${kind}-${Date.now()}.${ext}`;
    const storageRef = ref(storage, `vehicles/${safeReg}/${name}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  }
};

